<script setup lang="ts">
import GlobalLoader from "@/app/components/ui/GlobalLoader.vue"
import { useFingerprint } from "@/app/composables/useFingerprint"
import { useScreenSize } from "@/app/composables/useScreenSize"
import { brands } from "@/app/utils/brands"
import { useTranslatedLogo } from "@/casino/composables/useBannerTranslation"
import { useUserService } from "@/user-account/composables/useUserService"
import Intercom from "@intercom/messenger-js-sdk"
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useMeta } from "vue-meta"
import { useRoute, useRouter } from "vue-router"
import { useBetStore } from "../store.js"
import AppMobileMenu from "./app/components/mobile-menu/AppMobileMenu.vue"
import LanguageModal from "./app/components/modal/LanguageModal.vue"
import { useLanguageModal } from "./app/composables/useLanguageModal"

const { screenFactor, initScreenTracker } = useScreenSize()
const route = useRoute()
const store = useBetStore()
const { logo } = useTranslatedLogo()
const router = useRouter()
const { isLanguageModalOpen, closeLanguageModal } = useLanguageModal()

const { user, isAuthenticated } = useUserService()
const INTERCOM_APP_ID = ref(import.meta.env.VITE_APP_INTERCOM_APP_ID || null)

function removeSplashScreen() {
  setTimeout(() => {
    document.querySelector(".splash-screen")?.classList.add("fade-out")
    setTimeout(() => {
      document.querySelector("body").classList.remove("splash")
      document.body.style.position = "initial"
    }, 500)
  }, 1000)
}

initScreenTracker({
  mobile: 480,
  tablet: 766,
  notebook: 1280,
  desktop: 10000,
})

function initIntercom() {
  if (INTERCOM_APP_ID.value) {
    if (isAuthenticated() && user.value) {
      const createdAtTimestamp = Math.floor(new Date(user.value.createdAt).getTime() / 1000)
      Intercom({
        app_id: INTERCOM_APP_ID.value,
        region: "eu",
        user_id: user.value.id,
        name: user.value.username,
        email: user.value.email,
        created_at: createdAtTimestamp,
      })
    }
    else {
      Intercom({
        app_id: INTERCOM_APP_ID.value,
        region: "eu",
      })
    }
  }
}

watch(
  [isAuthenticated, user],
  ([newIsAuth, _], [oldIsAuth]) => {
    if (newIsAuth !== oldIsAuth) {
      initIntercom()
    }
  },
  { immediate: true },
)

function storeAffiliateTags() {
  const urlParams = new URLSearchParams(window.location.search)

  const bTag = urlParams.get("btag")

  if (!bTag) {
    return
  }

  localStorage.setItem("btag", JSON.stringify({ tag: bTag, date: new Date() }))
}

onMounted(async () => {
  storeAffiliateTags()
  window.addEventListener("resize", handleResize)
  removeSplashScreen()

  // Set og:image meta tag directly
  const metaImage = document.querySelector("meta[property=\"og:image\"]")
  if (metaImage) {
    metaImage.setAttribute("content", logo.value)
  }
  else {
    const newMetaImage = document.createElement("meta")
    newMetaImage.setAttribute("property", "og:image")
    newMetaImage.setAttribute("name", "meta")
    newMetaImage.setAttribute("content", logo.value)
    document.head.appendChild(newMetaImage)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

function handleResize() {
  store.setScreenWidth(window.innerWidth)
}

const isVisibleMobileMenu = computed(() => route.name !== "maintenance")

document.body.classList.add(`brand-${import.meta.env.VITE_APP_BRAND}`)

const brand = import.meta.env.VITE_APP_BRAND
const brandMeta = brands[brand]?.meta || {}

// Still use vue-meta for other meta tags
useMeta({
  title: brandMeta.title,
  link: [
    { rel: "icon", type: "image/x-icon", href: `${window.location.origin}${brandMeta.favicon}` },
    { rel: "apple-touch-icon", href: `${window.location.origin}${brandMeta.favicon}` },
  ],
  meta: [
    { property: "og:url", content: import.meta.env.VITE_APP_URL },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: brandMeta.title,
    },
    {
      property: "og:description",
      content: brandMeta.description,
    },
    { property: "og:image", content: `${window.location.origin}${brandMeta.image}` },
  ],
})

watch(
  () => route.query.action,
  (action) => {
    if (action === "open-deposit") {
      store.handleShowBetModal()
      store.walletTab = "deposit"
    }
    router.push({ query: {} })
  },
  { immediate: true },
)
// Initialize fingerprint tracking
useFingerprint()
</script>

<template>
  <metainfo />
  <main :class="screenFactor">
    <router-view />
    <AppMobileMenu v-if="isVisibleMobileMenu" class="tablet-only" />
    <GlobalLoader />

    <!-- Global Language Modal -->
    <LanguageModal
      :show="isLanguageModalOpen"
      @close="closeLanguageModal"
    />
  </main>
</template>

<style></style>
